import {
  CLOSE_CODE_BANNED,
  CLOSE_CODE_FLOOD,
  CLOSE_CODE_KICKED,
  type ModerationRejected,
  type ModerationResult,
  type ReportReason,
  type ReportRejected,
} from '@shefira/shared';

/** What the chat says about moderation and reports (ADR 0037). */

export const MODERATION_REFUSAL_TEXT: Record<ModerationRejected['reason'], string> = {
  invalid: 'Commande invalide.',
  'not-admin': 'Seul un maître du jeu peut faire ça.',
  'not-found': "Personne de ce nom n'existe.",
  self: 'Tu ne peux pas te sanctionner toi-même.',
  'admin-target': 'Un maître du jeu ne se sanctionne pas depuis le jeu.',
  offline: "Ce joueur n'est pas en jeu.",
  'no-sanction': 'Aucune sanction à lever.',
};

export const REPORT_REFUSAL_TEXT: Record<ReportRejected['reason'], string> = {
  invalid: 'Signalement refusé.',
  'not-found': "Personne de ce nom n'existe.",
  self: 'Tu ne peux pas te signaler toi-même.',
  'too-soon': 'Attends une minute avant de signaler à nouveau.',
};

export const REPORT_REASON_LABELS: Record<ReportReason, string> = {
  spam: 'Spam',
  harcelement: 'Harcèlement',
  triche: 'Triche',
  nom: 'Nom inapproprié',
  autre: 'Autre',
  remboursement: 'Achat remboursé',
};

/** « 14:32 », in the player's clock. */
export function clockText(at: number): string {
  return new Date(at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** « 3 oct. 2026, 14:32 »: a ban's end is days away, the day matters more than the hour. */
export function dateText(at: number): string {
  return new Date(at).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function moderationResultText(result: ModerationResult): string {
  switch (result.action) {
    case 'mute':
      return result.until === null
        ? `${result.name} est réduit au silence.`
        : `${result.name} est réduit au silence jusqu'à ${clockText(result.until)}.`;
    case 'unmute':
      return `${result.name} peut de nouveau parler.`;
    case 'kick':
      return `${result.name} a été renvoyé.`;
    case 'ban':
      return result.until === null
        ? `${result.name} est banni pour toujours.`
        : `${result.name} est banni jusqu'au ${dateText(result.until)}.`;
    case 'unban':
      return `${result.name} n'est plus banni.`;
  }
}

/** To a muted player: the server says until when. */
export function mutedText(until: number): string {
  return `Tu es réduit au silence jusqu'à ${clockText(until)}.`;
}

/**
 * Why the server closed the session, for the codes it closes on purpose
 * (ADR 0037, ADR 0040): the client shows this and never reconnects.
 */
export function closeCodeText(code: number): string | undefined {
  switch (code) {
    case CLOSE_CODE_KICKED:
      return "Un maître du jeu t'a renvoyé du jeu.";
    case CLOSE_CODE_BANNED:
      return 'Ce compte est banni.';
    case CLOSE_CODE_FLOOD:
      return 'Connexion coupée : trop de messages envoyés.';
    default:
      return undefined;
  }
}
